/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import Table from '../../components/common/Table';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/common/Loading';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import swal from 'sweetalert2';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const DesktopOnly = styled.div`
  width: 100%;
  @media (max-width: 768px) { display: none; }
`;

const MobileMenuBtn = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #1c1c1e;
    border-radius: 10px;
    color: #f5f5f7;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

const Container = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 94vh;
  overflow: scroll;
  width: 100%;
  margin: 16px 16px 16px 0;
  padding: 24px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    height: 100vh;
    margin: 0;
    border-radius: 0;
    padding: 16px;
  }
`;

const Toolbar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
`;

const SearchInput = styled.input`
  padding: 7px 14px;
  border: 1.5px solid #d2d2d7;
  border-radius: 8px;
  font-size: 14px;
  width: 260px;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s;
  &:focus { border-color: #007aff; }
`;

const Btn = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  background: ${({ $primary, $excel }) =>
    $excel ? '#1d6f42' : $primary ? '#007aff' : '#f3f4f6'};
  color: ${({ $primary, $excel }) =>
    ($primary || $excel) ? '#fff' : '#374151'};
  &:hover { opacity: 0.85; }
`;

const TotalText = styled.span`
  font-size: 14px;
  color: #86868b;
  font-weight: 500;
`;

const FilterSelect = styled.select`
  display: none;
  @media (max-width: 768px) {
    display: block;
    padding: 7px 10px;
    border: 1.5px solid #d2d2d7;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s;
    &:focus { border-color: #007aff; }
  }
`;

const SegmentGroup = styled.div`
  display: inline-flex;
  border: 1.5px solid #d2d2d7;
  border-radius: 8px;
  overflow: hidden;
  @media (max-width: 768px) { display: none; }
`;

const SegmentBtn = styled.button`
  padding: 6px 12px;
  border: none;
  border-right: 1px solid #d2d2d7;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $active }) => $active ? '#007aff' : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#6b7280'};
  &:last-child { border-right: none; }
  &:hover { background: ${({ $active }) => $active ? '#0066d6' : '#f3f4f6'}; }
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #86868b;
  margin-right: 4px;
  @media (max-width: 768px) { display: none; }
`;

const ImgOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.6);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ImgPreview = styled.img`
  max-width: 80vw;
  max-height: 80vh;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.3);
  object-fit: contain;
  background: #fff;
`;

const stockStatusMap = {
  normal: { label: '정상', color: '#065f46', bg: '#ecfdf5' },
  sold_out: { label: '품절', color: '#dc2626', bg: '#fef2f2' },
  discontinued: { label: '단종', color: '#6b7280', bg: '#f3f4f6' },
};

const StatusCell = ({ status }) => {
  const info = stockStatusMap[status] || stockStatusMap.normal;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: info.bg,
      color: info.color,
      whiteSpace: 'nowrap',
    }}>
      {info.label}
    </span>
  );
};

const statusLabelMap = { normal: '정상', sold_out: '품절', discontinued: '단종' };

const ProductListPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState();
  const [allProducts, setAllProducts] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [previewImg, setPreviewImg] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const fileRef = useRef(null);

  const getProducts = async () => {
    try {
      setIsLoading(true);
      let { data: res } = await axios.get('/admin/products/productlist');
      setAllProducts(res);
      applyFilter(res, '', 'all', 'all');
      setIsLoading(false);
    } catch (e) {
      console.log(e, 'getList');
      setIsLoading(false);
    }
  };

  const applyFilter = (data, query, status, stock) => {
    let filtered = data;
    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter((r) =>
        (r.product_name || '').toLowerCase().includes(q) ||
        (r.main_category || '').toLowerCase().includes(q) ||
        (r.sub_category || '').toLowerCase().includes(q)
      );
    }
    if (status && status !== 'all') {
      filtered = filtered.filter((r) => (r.stock_status || 'normal') === status);
    }
    if (stock === 'has_stock') {
      filtered = filtered.filter((r) => r.stock_quantity != null && r.stock_quantity > 0);
    } else if (stock === 'no_stock') {
      filtered = filtered.filter((r) => r.stock_quantity != null && r.stock_quantity === 0);
    } else if (stock === 'unset') {
      filtered = filtered.filter((r) => r.stock_quantity == null);
    }
    const ids = filtered.map((r) => r._id);
    const rows = filtered.map((r) => ({
      img: r.img ? <img src={r.img} alt="" onClick={(e) => { e.stopPropagation(); setPreviewImg(r.img); }} style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', background: '#f5f5f7', border: '1px solid #e5e7eb', cursor: 'zoom-in' }} /> : <div style={{ width: 56, height: 56, borderRadius: 10, background: '#f5f5f7', border: '1px solid #e5e7eb' }} />,
      product_name: r.product_name,
      stock_status: <StatusCell status={r.stock_status} />,
      stock_quantity: r.stock_quantity != null ? r.stock_quantity : '-',
      category: <span style={{ color: '#86868b', fontSize: 12 }}>{r.main_category} &gt; {r.sub_category}</span>,
      order_count: r.order_count || 0,
    }));
    setProductIds(ids);
    setProducts(rows);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilter(allProducts, searchInput, statusFilter, stockFilter);
  };

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    applyFilter(allProducts, searchInput, val, stockFilter);
  };

  const handleStockFilter = (val) => {
    setStockFilter(val);
    applyFilter(allProducts, searchInput, statusFilter, val);
  };

  const handleReset = () => {
    setSearchInput('');
    setStatusFilter('all');
    setStockFilter('all');
    applyFilter(allProducts, '', 'all', 'all');
  };

  const handleRowClick = (index) => {
    if (productIds[index]) {
      navigate(`/product/list/detail/${productIds[index]}`);
    }
  };

  const handleExcelDownload = () => {
    if (!allProducts.length) return;
    const rows = allProducts.map((r) => ({
      '_id': r._id,
      '제조사': r.main_category,
      '카테고리': r.sub_category,
      '제품명': r.product_name,
      '상태': statusLabelMap[r.stock_status] || '정상',
      '재고': r.stock_quantity != null ? r.stock_quantity : '',
      'VIP': r.vip || 0,
      'Mall': r.mall || 0,
      'Level1': r.level1 || 0,
      'Level2': r.level2 || 0,
      'Level3': r.level3 || 0,
      'School': r.school || 0,
      '설명': r.description || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 26 }, { wch: 15 }, { wch: 20 }, { wch: 40 },
      { wch: 8 }, { wch: 8 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
      { wch: 30 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '상품목록');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), '상품목록.xlsx');
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    const confirm = await swal.fire({
      title: '상품 일괄 업로드',
      html: '_id가 있는 행은 수정, 없으면 신규 등록됩니다.',
      icon: 'question',
      confirmButtonText: '업로드',
      cancelButtonText: '취소',
      confirmButtonColor: '#007aff',
      cancelButtonColor: '#a3a3a3',
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post('/admin/products/product-bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setIsLoading(false);
      let html = `${data.updated}건 수정, ${data.created}건 신규, ${data.skipped}건 건너뜀`;
      if (data.errors && data.errors.length > 0) {
        html += `<br><br><div style="text-align:left;font-size:12px;color:#dc2626;max-height:120px;overflow:auto">${data.errors.join('<br>')}</div>`;
      }
      swal.fire({ title: '완료', html, icon: 'success', confirmButtonColor: '#007aff' });
      getProducts();
    } catch (err) {
      setIsLoading(false);
      const msg = err.response?.data?.message || '오류가 발생했습니다.';
      swal.fire({ text: msg, icon: 'error', confirmButtonColor: '#007aff' });
    }
  };

  const titles = ['', '제품명', '상태', '재고', '카테고리', '주문'];

  useEffect(() => {
    getProducts();
  }, []);

  return (
    <Container>
      <div style={{ width: '100%', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
        <MobileMenuBtn onClick={() => window.dispatchEvent(new Event('open-sidebar'))}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </MobileMenuBtn>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>제품 조회</h2>
      </div>
      <DesktopOnly>
      <Toolbar>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
            <SearchInput
              placeholder="제품명, 제조사, 카테고리 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Btn $primary type="submit">검색</Btn>
          </form>
          <FilterLabel>상태</FilterLabel>
          <SegmentGroup>
            {[{ value: 'all', label: '전체' }, { value: 'normal', label: '정상' }, { value: 'sold_out', label: '품절' }, { value: 'discontinued', label: '단종' }].map((opt) => (
              <SegmentBtn key={opt.value} $active={statusFilter === opt.value} onClick={() => handleStatusFilter(opt.value)}>
                {opt.label}
              </SegmentBtn>
            ))}
          </SegmentGroup>
          <FilterLabel>재고</FilterLabel>
          <SegmentGroup>
            {[{ value: 'all', label: '전체' }, { value: 'has_stock', label: '있음' }, { value: 'no_stock', label: '0' }, { value: 'unset', label: '미설정' }].map((opt) => (
              <SegmentBtn key={opt.value} $active={stockFilter === opt.value} onClick={() => handleStockFilter(opt.value)}>
                {opt.label}
              </SegmentBtn>
            ))}
          </SegmentGroup>
          <FilterSelect value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)}>
            <option value="all">상태: 전체</option>
            <option value="normal">정상</option>
            <option value="sold_out">품절</option>
            <option value="discontinued">단종</option>
          </FilterSelect>
          <FilterSelect value={stockFilter} onChange={(e) => handleStockFilter(e.target.value)}>
            <option value="all">재고: 전체</option>
            <option value="has_stock">재고 있음</option>
            <option value="no_stock">재고 0</option>
            <option value="unset">미설정</option>
          </FilterSelect>
          {(searchInput || statusFilter !== 'all' || stockFilter !== 'all') && (
            <Btn onClick={handleReset}>초기화</Btn>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <TotalText>총 {products ? products.length : 0}개</TotalText>
          <Btn $primary onClick={() => fileRef.current?.click()}>엑셀 업로드</Btn>
          <Btn $excel onClick={handleExcelDownload}>엑셀 다운로드</Btn>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} style={{ display: 'none' }} />
        </div>
      </Toolbar>
      </DesktopOnly>
      {products && products.length > 0 ? (
        <Table
          titles={titles}
          data={products}
          onRowClick={handleRowClick}
        />
      ) : !isLoading && (
        <div style={{ color: '#86868b', fontSize: 15, padding: '80px 0', textAlign: 'center', width: '100%' }}>
          {searchInput ? '검색 결과가 없습니다.' : '제품이 없습니다.'}
        </div>
      )}
      {isLoading && <Loading />}
      {previewImg && (
        <ImgOverlay onClick={() => setPreviewImg(null)}>
          <ImgPreview src={previewImg} onClick={(e) => e.stopPropagation()} />
        </ImgOverlay>
      )}
    </Container>
  );
};

export default ProductListPage;
